import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Table, Input, Select, Button, Card, Space, Popconfirm, Typography, Tabs } from "antd";
import { DeleteOutlined, ArrowRightOutlined, UserOutlined, MailOutlined } from "@ant-design/icons";
import Layout from "../../ReusableComponents/Layout";
import getAllUsers from "../../Redux/APIs/usersAPIs/get";
import getAllGuests from "../../Redux/APIs/GuestAPIs/get";
import deleteUser from "../../Redux/APIs/usersAPIs/delete";
import { format, parseISO } from "date-fns";
import PaginationComponent from "../../ReusableComponents/Pagination/PaginationComponent";
import LetterStatusIcon from "../GuestDetails/components/LetterStatusIcon";
import { getCompanyStatus } from "../../Utils/statusUtils";

const { Text } = Typography;

const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
};

const toolbarStyle = {
  padding: "16px 24px",
  borderBottom: "1px solid #f0f0f0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
};

const StatusPill = ({ status }) => {
  const config = {
    Completed: { bg: "#f0fdf4", text: "#15803d", dot: "#22c55e", label: "Completed" },
    "Action needed": { bg: "#fffbeb", text: "#b45309", dot: "#f59e0b", label: "Action Needed" },
    inProgress: { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6", label: "In Progress" },
  };
  const c = config[status] || config.inProgress;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 10,
        backgroundColor: c.bg,
        color: c.text,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "20px",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: c.dot }} />
      {c.label}
    </span>
  );
};

const PaidPill = ({ paid }) => {
  const bg = paid ? "#f0fdf4" : "#fef2f2";
  const color = paid ? "#15803d" : "#dc2626";
  const dot = paid ? "#22c55e" : "#ef4444";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 10,
        backgroundColor: bg,
        color,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "20px",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: dot }} />
      {paid ? "Paid" : "Unpaid"}
    </span>
  );
};

const UsersTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [paginationResult, setPaginationResult] = useState({
    totalPages: 0, totalItems: 0, itemsPerPage: 0, currentPage: 0,
  });
  const [users, setUsers] = useState([]);
  const [filterPrinted, setFilterPrinted] = useState("none");
  const [filterStatus, setFilterStatus] = useState("none");
  const [searchTerm, setSearchTerm] = useState("");

  const filterUserDeletion = (id) => {
    setUsers((prev) => prev.filter((user) => user._id !== id));
    setPaginationResult((prev) => ({
      ...prev,
      totalItems: Math.max(0, (prev.totalItems ?? 0) - 1),
    }));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      dispatch(getAllUsers(setUsers, setPaginationResult, page, searchTerm, filterPrinted, filterStatus));
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, page, filterPrinted, filterStatus]);

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => (
        <Space size={8}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "#eff6ff",
              color: "#3b82f6",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {r.firstName?.[0]?.toUpperCase() || "?"}
          </span>
          <span style={{ textTransform: "capitalize", fontWeight: 500 }}>
            {r.firstName} {r.lastName}
          </span>
        </Space>
      ),
    },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Orgs</span>,
      key: "orgs",
      align: "center",
      width: 80,
      render: (_, r) => r?.moveinInfo?.[0]?.companies?.length ?? 0,
    },
    {
      title: "Created",
      key: "createdAt",
      align: "center",
      width: 100,
      render: (_, r) => r?.createdAt ? format(parseISO(r.createdAt), "dd MMM yy") : "-",
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Move Date</span>,
      key: "moveDate",
      align: "center",
      width: 120,
      render: (_, r) =>
        r?.moveinInfo?.[0]?.movingDate
          ? format(parseISO(r.moveinInfo[0].movingDate), "dd MMM yy")
          : "-",
    },
    {
      title: "Paid",
      key: "paid",
      align: "center",
      width: 90,
      render: (_, r) => <PaidPill paid={r?.paid} />,
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Letters</span>,
      key: "letters",
      align: "center",
      width: 100,
      render: (_, r) => (
        <Space size={4} align="center">
          <LetterStatusIcon status={r?.oldAddressPrinted} size={16} />
          <ArrowRightOutlined style={{ fontSize: 10, color: "#94a3b8" }} />
          <LetterStatusIcon status={r?.newAddressPrinted} size={16} />
        </Space>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      width: 130,
      render: (_, r) => <StatusPill status={getCompanyStatus(r?.moveinInfo?.[0]?.companies)} />,
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, r) => (
        <Popconfirm
          title="Are you sure you want to delete this user?"
          onConfirm={(e) => {
            e?.stopPropagation?.();
            dispatch(deleteUser(r._id, filterUserDeletion));
          }}
          onCancel={(e) => e?.stopPropagation?.()}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ borderRadius: 6 }}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card style={cardStyle} styles={{ body: { padding: 0 } }}>
      <div style={toolbarStyle}>
        <Space size={12} wrap>
          <Input.Search
            placeholder="Name, Email, Phone"
            allowClear
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            onSearch={() => {}}
            style={{ width: 280 }}
          />
          {searchTerm.length === 0 && (
            <Select
              value={filterStatus}
              onChange={(v) => { setFilterStatus(v); setPage(0); }}
              style={{ width: 160 }}
              options={[
                { value: "none", label: "All Statuses" },
                { value: "Completed", label: "Completed" },
                { value: "Action needed", label: "Action Needed" },
                { value: "inProgress", label: "In Progress" },
              ]}
            />
          )}
          <Select
            value={filterPrinted}
            onChange={(v) => { setFilterPrinted(v); setPage(0); }}
            style={{ width: 160 }}
            options={[
              { value: "none", label: "All Letters" },
              { value: "only_old", label: "Old not printed" },
              { value: "only_new", label: "New not printed" },
              { value: "both", label: "Both not printed" },
            ]}
          />
        </Space>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {paginationResult.totalItems ?? 0} records
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="_id"
        pagination={false}
        onRow={(record) => ({
          onClick: () => navigate(`/user-details/${record._id}`),
          style: { cursor: "pointer" },
        })}
        locale={{ emptyText: "No users found." }}
      />

      {searchTerm.length === 0 && (
        <PaginationComponent {...paginationResult} setPage={setPage} />
      )}
    </Card>
  );
};

const GuestsTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [paginationResult, setPaginationResult] = useState({
    totalPages: 0, totalItems: 0, itemsPerPage: 0, currentPage: 0,
  });
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [oldTown, setOldTown] = useState("");
  const [newTown, setNewTown] = useState("");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      dispatch(getAllGuests(setUsers, setPaginationResult, page, searchTerm, oldTown, newTown));
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, oldTown, newTown, page]);

  const columns = [
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Orgs</span>,
      key: "orgs",
      align: "center",
      width: 80,
      render: (_, r) => r?.companies?.length,
    },
    {
      title: "Created",
      key: "createdAt",
      align: "center",
      width: 100,
      render: (_, r) => r?.createdAt ? format(parseISO(r.createdAt), "dd MMM yy") : "-",
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Move Date</span>,
      key: "moveDate",
      align: "center",
      width: 120,
      render: (_, r) => r?.movingDate ? format(parseISO(r.movingDate), "dd MMM yy") : "-",
    },
    {
      title: "New Town",
      key: "newTown",
      render: (_, r) => r?.newAddress?.posttown,
    },
    {
      title: "Old Town",
      key: "oldTown",
      render: (_, r) => r?.oldAddress?.posttown,
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Letters</span>,
      key: "letters",
      align: "center",
      width: 100,
      render: (_, r) => (
        <Space size={4} align="center">
          <LetterStatusIcon status={r?.isLetterPrinted} size={16} />
          <ArrowRightOutlined style={{ fontSize: 10, color: "#94a3b8" }} />
          <LetterStatusIcon status={r?.isNewLetterPrinted} size={16} />
        </Space>
      ),
    },
  ];

  return (
    <Card style={cardStyle} styles={{ body: { padding: 0 } }}>
      <div style={toolbarStyle}>
        <Space size={12} wrap>
          <Input.Search
            placeholder="Email"
            allowClear
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            style={{ width: 200 }}
          />
          <Input
            placeholder="New Town"
            allowClear
            value={newTown}
            onChange={(e) => { setNewTown(e.target.value); setPage(0); }}
            style={{ width: 140 }}
          />
          <Input
            placeholder="Old Town"
            allowClear
            value={oldTown}
            onChange={(e) => { setOldTown(e.target.value); setPage(0); }}
            style={{ width: 140 }}
          />
        </Space>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {paginationResult.totalItems ?? 0} records
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="_id"
        pagination={false}
        onRow={(record) => ({
          onClick: () => navigate(`/guest-details/${record._id}`),
          style: { cursor: "pointer" },
        })}
        locale={{ emptyText: "No guests found." }}
      />

      <PaginationComponent {...paginationResult} setPage={setPage} />
    </Card>
  );
};

const CompletedUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "users";

  const tabItems = [
    {
      key: "users",
      label: <span><UserOutlined /> Users</span>,
      children: <UsersTab />,
    },
    {
      key: "guests",
      label: <span><MailOutlined /> Guest Users</span>,
      children: <GuestsTab />,
    },
  ];

  return (
    <Layout>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key })}
        items={tabItems}
        size="large"
      />
    </Layout>
  );
};

export default CompletedUsers;
